import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, MapPinIcon } from 'lucide-react';
import type { Audiencia } from '../../types';
import { abogadoPorId } from '../../data/abogados';
import { useDatos } from '../../contexts/DatosContext';
import { MarcaGoogle } from '../ui/Google';
import { etiquetaAnticipacion } from '../../utils/google';
import { diasRestantes, etiquetaDias, fechaCorta } from '../../utils/fechas';
import { EstadoVacio } from '../ui/Basicos';
function Fila({
  a,
  pasada
}: {
  a: Audiencia;
  pasada: boolean;
}) {
  const {
    eventoGoogleDe,
    conexionGoogleDe
  } = useDatos();
  const evento = eventoGoogleDe('audiencia', a.id);
  const conexion = conexionGoogleDe(a.abogadoId);
  return <li className="flex items-start gap-4 px-5 py-3.5" data-id="element-535" data-mp="1vqtkwqtk5j4b">
      <span className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded ${pasada ? 'bg-gris-100 text-gris-400' : a.tipo === 'Audiencia' ? 'bg-marino-800 text-white' : 'bg-ambar-50 text-ambar-700'}`} data-id="element-536" data-mp="1vtfyp871kuuz">
        <CalendarDaysIcon className="h-4 w-4" aria-hidden="true" data-id="element-537" data-mp="1n8ah5p5n2tsv" />
      </span>
      <div className="min-w-0 flex-1" data-id="element-538" data-mp="1n66fldv7q6vr">
        <p className={`flex items-center gap-2 text-sm font-medium ${pasada ? 'text-gris-500' : 'text-gris-800'}`} data-id="element-539" data-mp="21xos31ov5jad">
          {a.titulo}
          {evento && <MarcaGoogle titulo={`En Google Calendar de ${conexion.correoGoogle} · recordatorio ${etiquetaAnticipacion(evento.recordatorioMinutos)}`} data-id="element-540" data-mp="197zrgpg2fl9a" />}
        </p>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gris-500" data-id="element-541" data-mp="1i9g0asr8ffbe">
          <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-542" data-mp="1e7uf8k4jnuq4" />
          {a.lugar} · {a.autoridad}
        </p>
        <p className="mt-0.5 text-2xs text-gris-400" data-id="element-543" data-mp="2tnl5fp36lv8b">
          A cargo de {abogadoPorId(a.abogadoId)?.nombre} · {a.tipo}
        </p>
        {!pasada && !evento && conexion.estado !== 'conectado' && <p className="mt-1 text-2xs text-ambar-700" data-id="element-544" data-mp="1mashxqdwfvk0">
            No se envió a Google Calendar:{' '}
            <Link to={`/abogados/${a.abogadoId}`} className="font-semibold underline" data-id="element-545" data-mp="1r9vppil9kfvo">
              conectar la cuenta de Google
            </Link>{' '}
            de esta persona.
          </p>}
      </div>
      <div className="w-32 shrink-0 text-right" data-id="element-546" data-mp="20b3aqyyrrk7t">
        <p className="tabular text-xs font-medium text-gris-700" data-id="element-547" data-mp="13lm6moffq688">{fechaCorta(a.fecha)}</p>
        <p className="tabular text-2xs text-gris-500" data-id="element-548" data-mp="18kgptrhbdwsv">{a.hora} h</p>
        {!pasada && <p className="mt-0.5 text-2xs font-semibold text-marino-700" data-id="element-549" data-mp="18i2t7e6zkcj7">
            {etiquetaDias(diasRestantes(a.fecha))}
          </p>}
      </div>
    </li>;
}
export function ListaAudiencias({
  audiencias
}: {
  audiencias: Audiencia[];
}) {
  const proximas = audiencias.filter(a => diasRestantes(a.fecha) >= 0);
  const pasadas = audiencias.filter(a => diasRestantes(a.fecha) < 0).reverse();
  if (audiencias.length === 0) {
    return <EstadoVacio mensaje="Sin audiencias ni citas registradas" data-id="element-550" data-mp="hbvzblti5zr1" />;
  }
  return <div data-id="element-551" data-mp="cdcclj734jxm">
      <p className="border-b border-gris-200 bg-gris-50 px-5 py-2 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-552" data-mp="cdlyovp0e6y1">
        Próximas ({proximas.length})
      </p>
      {proximas.length === 0 ? <p className="px-5 py-4 text-xs text-gris-400" data-id="element-553" data-mp="3ce3wnlpbo8p">No hay eventos futuros programados.</p> : <ul className="divide-y divide-gris-100" data-id="element-554" data-mp="3uk8rgs2ynwa9">
          {proximas.map(a => <Fila key={a.id} a={a} pasada={false} data-id="element-555" data-mp="3prpd3swg8x4e" />)}
        </ul>}
      <p className="border-y border-gris-200 bg-gris-50 px-5 py-2 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-556" data-mp="3kt5o1jmk757t">
        Celebradas ({pasadas.length})
      </p>
      {pasadas.length === 0 ? <p className="px-5 py-4 text-xs text-gris-400" data-id="element-557" data-mp="3brxt9bj94mih">Sin antecedentes registrados.</p> : <ul className="divide-y divide-gris-100" data-id="element-558" data-mp="36uxn70mejjrd">
          {pasadas.map(a => <Fila key={a.id} a={a} pasada data-id="element-559" data-mp="1aq84c489y0np" />)}
        </ul>}
    </div>;
}